import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

export default function HeroSection() {
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const sites = useCountUp(15, 1500, statsVisible);
  const satisfaction = useCountUp(100, 1800, statsVisible);
  const hours = useCountUp(48, 1200, statsVisible);

  const scrollToAdvantages = () => {
    const el = document.getElementById('avantages');
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 72;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 overflow-hidden">
      {/* Image de fond */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/2b36f85bee70e2f72c4930f7ee02a2ef.jpg"
          alt="Background"
          className="w-full h-full object-cover object-center"
          loading="eager"
          width="1920"
          height="1080"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/80"></div>
      </div>

      {/* Éléments décoratifs */}
      <div className="absolute top-32 right-16 w-[500px] h-[500px] bg-emerald-500/8 rounded-full blur-[140px] pointer-events-none"></div>
      <div className="absolute bottom-32 left-16 w-[350px] h-[350px] bg-teal-500/8 rounded-full blur-[120px] pointer-events-none"></div>

      {/* Contenu principal */}
      <div className="relative z-10 text-center max-w-5xl mx-auto w-full pt-28 sm:pt-36 pb-16">
        {/* Badge offre */}
        <div className="inline-flex items-center space-x-2 bg-emerald-500/20 backdrop-blur-md border border-emerald-400/30 text-emerald-300 px-5 py-2 rounded-full text-xs sm:text-sm font-bold mb-8 tracking-wider uppercase">
          <i className="ri-flashlight-fill text-emerald-400 text-base animate-pulse"></i>
          <span>Offre de Lancement — Places Limitées</span>
        </div>

        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
          Votre Site Pro Livré
          <br />
          <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-300 bg-clip-text text-transparent">
            En 7 Jours
          </span>
          {' '}— Dès{' '}
          <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-300 bg-clip-text text-transparent">
            69€/mois
          </span>
        </h1>

        <p className="text-base sm:text-xl text-white/65 mb-10 max-w-2xl mx-auto leading-relaxed font-medium">
          Design personnalisé · Optimisé Google &amp; Mobile · Formulaire de réservation intégré
          <br className="hidden sm:block" />
          <span className="text-emerald-400 font-semibold">Sans engagement · Réponse sous 24h</span>
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
          <Link
            to="/offres"
            className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-10 sm:px-14 py-4 sm:py-5 rounded-full text-base sm:text-xl font-black shadow-2xl shadow-emerald-500/40 hover:shadow-emerald-500/60 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-store-2-line text-lg"></i>
            <span>Voir Nos Offres</span>
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
          </Link>
          <Link
            to="/demande-devis"
            className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md border border-white/25 text-white px-8 py-4 rounded-full text-base font-bold hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-file-text-line text-lg"></i>
            <span>Devis Gratuit en 2 min</span>
          </Link>
        </div>

        {/* Stats animées */}
        <div
          ref={statsRef}
          className="grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl mx-auto"
        >
          {[
            { value: sites, suffix: '+', label: 'Sites livrés', icon: 'ri-global-line' },
            { value: satisfaction, suffix: '%', label: 'Clients satisfaits', icon: 'ri-heart-3-line' },
            { value: hours, suffix: 'h', label: 'Délai moyen', icon: 'ri-timer-flash-line' },
          ].map((stat, i) => (
            <div key={i} className="text-center bg-white/8 backdrop-blur-sm border border-white/12 rounded-2xl py-4 sm:py-5 px-2">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                <i className={`${stat.icon} text-emerald-400 text-xl`}></i>
              </div>
              <div className="text-2xl sm:text-4xl font-black text-white">
                {stat.value}{stat.suffix}
              </div>
              <div className="text-xs sm:text-sm text-white/50 font-medium mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToAdvantages}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40 hover:text-white/70 transition-colors cursor-pointer"
        aria-label="Défiler vers le bas"
      >
        <span className="text-xs font-medium tracking-widest uppercase">Découvrir</span>
        <div className="w-6 h-10 border-2 border-white/25 rounded-full flex items-start justify-center p-1.5">
          <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce"></div>
        </div>
      </button>
    </section>
  );
}
